import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
} from 'lucide-react';

import { ADMIN_PAGE_SIZES } from '@qaytim/shared';

import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import type { OffsetPager } from '@/hooks/use-offset-pager';
import { strings } from '@/i18n/strings';

export interface PagerControlsProps {
  pager: OffsetPager;
  /** Serverdan kelgan javob. `undefined` — hali yuklanmoqda; panel joyini bo'shatmaydi. */
  total: number | undefined;
  pageCount: number | undefined;
  /** Shu sahifada haqiqatda chizilgan qatorlar soni — oraliqni hisoblash uchun. */
  rowCount: number;
  busy?: boolean;
  /** Ekran o'quvchisi uchun: bitta sahifada bir nechta jadval bo'lishi mumkin. */
  label?: string;
}

function fill(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, key: string) => String(values[key] ?? ''));
}

/**
 * Sahifalash paneli — jami sanoq bilan.
 *
 * Ilgari bu komponent bitta sahifalik ro'yxat ostida UMUMAN chizilmasdi va bu to'g'ri qaror edi:
 * o'lik "Keyingi" tugmasi ro'yxat kesilganday taassurot qoldirardi. Endi u har doim turadi,
 * chunki panel endi faqat tugmalar emas — u "jami 340 ta" degan javobni ham olib yuradi, va
 * aynan bitta sahifalik ro'yxatda bu javob eng foydali: operator qidiruv yetarlicha
 * toraytirilganini shu raqamdan biladi.
 *
 * Tugmalar to'rttasi ataylab: faqat "oldinga/orqaga" bilan 12-sahifadan boshiga qaytish o'n bir
 * marta bosishni talab qilardi.
 */
export function PagerControls({
  pager,
  total,
  pageCount,
  rowCount,
  busy,
  label,
}: PagerControlsProps) {
  const resolvedPageCount = pageCount ?? 0;
  const isFirst = pager.page <= 1;
  const isLast = resolvedPageCount === 0 || pager.page >= resolvedPageCount;

  const from = rowCount === 0 ? 0 : (pager.page - 1) * pager.limit + 1;
  const to = rowCount === 0 ? 0 : from + rowCount - 1;

  const suffix = label === undefined ? '' : ` — ${label}`;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-3">
      <div className="text-muted-foreground flex flex-col gap-0.5 text-xs">
        <span className="tabular">
          {total === undefined || total === 0
            ? strings.pager.empty
            : fill(strings.pager.rangeOf, { from, to, total })}
        </span>
        {resolvedPageCount > 1 ? (
          <span className="tabular">
            {fill(strings.pager.pageOf, { page: pager.page, pageCount: resolvedPageCount })}
          </span>
        ) : null}
      </div>

      <div className="flex items-center gap-2">
        <label className="text-muted-foreground flex items-center gap-1.5 text-xs">
          {strings.pager.perPage}
          <Select
            className="h-8 w-auto"
            aria-label={`${strings.pager.perPage}${suffix}`}
            value={String(pager.limit)}
            onChange={(event) => pager.setLimit(Number(event.target.value))}
            disabled={busy}
          >
            {ADMIN_PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </Select>
        </label>

        <Button
          variant="outline"
          size="sm"
          aria-label={`${strings.pager.first}${suffix}`}
          onClick={() => pager.setPage(1)}
          disabled={isFirst || busy}
        >
          <ChevronsLeftIcon aria-hidden="true" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          aria-label={`${strings.pager.previous}${suffix}`}
          onClick={() => pager.setPage(pager.page - 1)}
          disabled={isFirst || busy}
        >
          <ChevronLeftIcon aria-hidden="true" />
          <span className="hidden sm:inline">{strings.common.previous}</span>
        </Button>
        <Button
          variant="outline"
          size="sm"
          aria-label={`${strings.pager.next}${suffix}`}
          onClick={() => pager.setPage(pager.page + 1)}
          disabled={isLast || busy}
        >
          <span className="hidden sm:inline">{strings.common.next}</span>
          <ChevronRightIcon aria-hidden="true" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          aria-label={`${strings.pager.last}${suffix}`}
          onClick={() => pager.setPage(resolvedPageCount)}
          disabled={isLast || busy}
        >
          <ChevronsRightIcon aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}
